import { useRef, useState } from "react";
import { Camera, Upload, Trash2, Image as ImageIcon, Loader2, Store } from "lucide-react";
import { toast } from "sonner";

/**
 * Image picker for pantry items. Two free sources (no AI image generation):
 *   1. Local file upload (FileReader → data-URL, max ~4 MB)
 *   2. Search OpenFoodFacts by name → real grocery-store product photo (free)
 *
 * Props:
 *   value (string)             – current image URL or data-URL
 *   onChange(url: string)      – setter; pass "" to clear
 *   prompt (string, optional)  – default search seed (usually the item name)
 *   compact (boolean, def=false) – render a smaller thumbnail
 */
export default function ItemImagePicker({ value, onChange, prompt = "", compact = false }) {
  const fileInputRef = useRef(null);
  const [searching, setSearching] = useState(false);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Filen er ikke et bilde");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Bildet er for stort (maks 4 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      onChange(String(reader.result || ""));
    };
    reader.onerror = () => toast.error("Kunne ikke lese filen");
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  // Search OpenFoodFacts (free, no key) by product name and use the *best-ranked*
  // matching product image — these are real grocery-store packaging photos
  // uploaded by the community. We score candidates by Norway-country match,
  // image quality, name-similarity and overall data completeness so we don't
  // pick e.g. random Brazilian variants when searching for "Kjøttdeig".
  const searchOFF = async () => {
    const seed = (prompt || "").trim();
    if (!seed) {
      toast.error("Skriv inn varenavn først");
      return;
    }
    setSearching(true);
    try {
      const seedLower = seed.toLowerCase();
      // Tokenize for word-overlap scoring; ignore tokens shorter than 3 chars.
      const seedTokens = seedLower.split(/[\s,.\-/]+/).filter((t) => t.length >= 3);

      // 1. Norway-preferred page  2. Global fallback. Concat results for ranking.
      const urls = [
        `https://world.openfoodfacts.org/cgi/search.pl?search_terms=${encodeURIComponent(seed)}&search_simple=1&action=process&json=1&page_size=12&sort_by=popularity_key&countries_tags_en=norway`,
        `https://world.openfoodfacts.org/cgi/search.pl?search_terms=${encodeURIComponent(seed)}&search_simple=1&action=process&json=1&page_size=12&sort_by=popularity_key`,
      ];
      const products = [];
      const seen = new Set();
      for (const u of urls) {
        try {
          const r = await fetch(u);
          if (!r.ok) continue;
          const j = await r.json();
          for (const p of j?.products || []) {
            const code = p?.code || p?._id;
            if (code && seen.has(code)) continue;
            if (code) seen.add(code);
            products.push(p);
          }
        } catch { /* skip page on network error */ }
      }
      if (products.length === 0) {
        toast.error("Fant ingen butikkbilete – prøv eit anna namn eller last opp eit eige bilde");
        return;
      }

      const pickField = (p) => p?.image_front_url || p?.image_url || p?.image_small_url || "";
      const scoreProduct = (p) => {
        if (!pickField(p)) return -1; // exclude products without an image
        let score = 0;
        // Norway-tag match → big bonus
        const tags = String(p?.countries_tags || "").toLowerCase();
        if (tags.includes("norway") || tags.includes("en:norway") || tags.includes("nb:norge")) score += 50;
        // Norwegian product name available → small bonus
        if ((p?.product_name_no || "").trim()) score += 10;
        // Front image always preferred over generic image_url
        if (p?.image_front_url) score += 15;
        // Has explicit brand → typically a real grocery product
        if ((p?.brands || "").trim()) score += 5;
        // Data completeness score from OFF itself (0–1)
        const compl = parseFloat(p?.completeness || 0);
        if (!Number.isNaN(compl)) score += compl * 20;
        // Name similarity: count overlap of seed tokens vs product_name tokens
        const name = `${p?.product_name_no || ""} ${p?.product_name || ""} ${p?.generic_name_no || ""}`.toLowerCase();
        let overlap = 0;
        for (const t of seedTokens) {
          if (name.includes(t)) overlap++;
        }
        if (seedTokens.length > 0) {
          score += (overlap / seedTokens.length) * 40;
        }
        // Quantity/packaging filled means it's a proper product entry
        if ((p?.quantity || "").trim()) score += 5;
        return score;
      };

      const ranked = products
        .map((p) => ({ p, s: scoreProduct(p) }))
        .filter((x) => x.s >= 0)
        .sort((a, b) => b.s - a.s);

      const top = ranked[0];
      if (!top) {
        toast.error("Fant ingen butikkbilete – prøv eit anna namn eller last opp eit eige bilde");
        return;
      }
      const img = pickField(top.p);
      onChange(img);
      const label = top.p.product_name_no || top.p.product_name || seed;
      toast.success(`Butikkbilde funne: ${label}`);
    } catch (err) {
      toast.error(err.message || "Kunne ikke søke OpenFoodFacts");
    } finally {
      setSearching(false);
    }
  };

  const clear = () => onChange("");

  const thumbSize = compact ? "h-16 w-16" : "h-24 w-24";

  return (
    <div className="space-y-2" data-testid="item-image-picker">
      <div className="flex items-start gap-3">
        <div className={`${thumbSize} rounded-lg border border-stone-200 bg-stone-50 flex items-center justify-center overflow-hidden flex-shrink-0`} data-testid="item-image-thumb">
          {value ? (
            <img src={value} alt="Vareforhåndsvisning" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon className="h-6 w-6 text-stone-300" />
          )}
        </div>
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          <button
            type="button"
            onClick={searchOFF}
            disabled={searching}
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-60"
            data-testid="item-image-off-search"
            title="Søk etter ekte butikkbilde på nett (OpenFoodFacts – gratis)"
          >
            {searching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Store className="h-3.5 w-3.5" />}
            {searching ? "Søker…" : "Hent butikkbilde"}
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-stone-200 bg-white px-2.5 py-2 text-xs font-medium text-stone-700 hover:bg-stone-50 transition-colors"
            data-testid="item-image-upload"
          >
            <Upload className="h-3.5 w-3.5" /> Last opp eige bilde
          </button>
          {value && (
            <button
              type="button"
              onClick={clear}
              className="col-span-1 sm:col-span-2 inline-flex items-center justify-center gap-1.5 rounded-md border border-stone-200 bg-white px-2.5 py-1.5 text-xs text-stone-500 hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors"
              data-testid="item-image-clear"
            >
              <Trash2 className="h-3.5 w-3.5" /> Fjern bilde
            </button>
          )}
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFile}
        className="hidden"
        data-testid="item-image-file-input"
      />
      <p className="text-[10px] text-stone-400 leading-tight">
        <Camera className="h-3 w-3 inline -mt-0.5 mr-0.5" />
        «Hent butikkbilde» søkjer etter ekte produktbilde på nett (OpenFoodFacts). Begge alternativ er gratis.
      </p>
    </div>
  );
}
